import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Autocomplete,
  Button,
  CircularProgress,
  Divider,
  IconButton,
  ListItemText,
  Menu,
  MenuItem,
  TextField,
  Typography,
} from "@mui/material";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import PrintIcon from "@mui/icons-material/Print";
import { toast } from "react-toastify";
import { useAuth } from "../context/AuthContext";
import { getInvoicesByPatientId, normalizeInvoiceRelationIds } from "../services/invoiceService";
import { getPatientById } from "../services/patientService";
import { printInvoiceFromTable } from "../utils/invoiceReprintUtils";
import {
  isExpenseInvoice,
  resolveInvoiceDisplayType,
} from "../utils/invoiceTableUtils";
import { toPeriodDateKey } from "../utils/patientPeriodSummary";
import { invoiceMatchesVisit, dedupeLabRecords } from "../utils/patientTimelineVisitUtils";
import { printPathologyLabReport } from "../pages/path-lab/labReportDisplayUtils";
import {
  fetchOrganizationProfile,
  getOrganizationId,
  getOrganizationName,
} from "../utils/organizationUtils";

const TYPE_LABELS = {
  REGISTRATION: "Registration",
  CONSULTATION: "Consultation",
  PATHOLOGYTEST: "Pathology Test",
  MEDICINE: "Medicine",
  DISCHARGE: "Discharge Bill",
  DONATION: "Donation",
  MISC: "Misc",
  OTHER: "Invoice",
};

const TYPE_ORDER = [
  "REGISTRATION",
  "CONSULTATION",
  "PATHOLOGYTEST",
  "MEDICINE",
  "DISCHARGE",
  "DONATION",
  "MISC",
  "OTHER",
];

const getInvoiceRawType = (invoice) =>
  String(invoice?.invoiceType || invoice?.invoice_type || "")
    .trim()
    .toUpperCase();

/** Prefer raw billing type so admission-linked medicine stays "Medicine". */
const getPatientInvoicePrintKey = (invoice) => {
  const rawType = getInvoiceRawType(invoice);
  if (
    rawType === "REGISTRATION" ||
    rawType === "CONSULTATION" ||
    rawType === "PATHOLOGYTEST" ||
    rawType === "PATHOLOGY" ||
    rawType === "MEDICINE" ||
    rawType === "DONATION" ||
    rawType === "MISC"
  ) {
    return rawType === "PATHOLOGY" ? "PATHOLOGYTEST" : rawType;
  }
  if (invoice?.admissionId != null || invoice?.admission_id != null) {
    return "DISCHARGE";
  }
  const display = String(resolveInvoiceDisplayType(invoice) || "OTHER")
    .trim()
    .toUpperCase();
  return display || "OTHER";
};

const getPatientInvoicePrintLabel = (invoice) => {
  const key = getPatientInvoicePrintKey(invoice);
  return TYPE_LABELS[key] || key || "Invoice";
};

const getInvoiceNumber = (invoice) =>
  String(
    invoice?.invoiceNumber ||
      invoice?.invoice_number ||
      invoice?.id ||
      ""
  ).trim();

const invoiceMatchesScope = (invoice, { dateKey, admissionId, appointmentId, prescriptionId, prescriptionIds, appointmentDateTime, siblingAppointments } = {}) => {
  if (admissionId != null && admissionId !== "") {
    const invAdmission = invoice?.admissionId ?? invoice?.admission_id;
    if (invAdmission != null && String(invAdmission) === String(admissionId)) {
      return true;
    }
    if (dateKey) {
      const invDate = toPeriodDateKey(
        invoice?.date ?? invoice?.createdAt ?? invoice?.created_at
      );
      return invDate === dateKey;
    }
    return false;
  }

  return invoiceMatchesVisit(invoice, {
    dateKey,
    appointmentId,
    prescriptionIds:
      Array.isArray(prescriptionIds) && prescriptionIds.length > 0
        ? prescriptionIds
        : prescriptionId != null
        ? [prescriptionId]
        : [],
    appointmentDateTime,
    siblingAppointments,
  });
};

const resolveDoctorNameFromContext = (doctorName) => {
  const text = String(doctorName || "").trim();
  if (!text) return "";
  const lower = text.toLowerCase();
  if (lower === "n/a" || lower === "null" || lower === "-" || lower === "—") {
    return "";
  }
  return text;
};

const hasPrintableLabResult = (report) => {
  if (!report) return false;
  const result = report.result ?? report.testResult;
  if (result == null) return false;
  if (typeof result === "string") return result.trim().length > 0;
  if (typeof result === "object") return Object.keys(result).length > 0;
  return true;
};

/**
 * Timeline Print button with dropdown of summary + available patient invoices
 * for the current OPD day or IPD admission. Uses existing reprint formats.
 */
const PatientTimelinePrintMenu = ({
  patientId,
  patient = null,
  dateKey = null,
  admissionId = null,
  appointmentId = null,
  prescriptionId = null,
  prescriptionIds = null,
  appointmentDateTime = null,
  siblingAppointments = null,
  onPrintSummary,
  onExportPdf,
  labReports = null,
  doctorName = null,
  invoiceTypes = null,
  showLabReports = true,
  size = "small",
  variant = "outlined",
  color = "primary",
  iconOnly = false,
  hideTrigger = false,
  externalAnchorEl = null,
  externalOpen = false,
  onMenuClose,
  asField = false,
  fieldLabel = "Select document",
  sx,
}) => {
  const { user } = useAuth();
  const [anchorEl, setAnchorEl] = useState(null);
  const [loading, setLoading] = useState(false);
  const [printing, setPrinting] = useState(false);
  const [invoices, setInvoices] = useState([]);

  const open = Boolean(anchorEl);

  const loadInvoices = useCallback(async () => {
    if (patientId == null || patientId === "" || patientId === "undefined") {
      setInvoices([]);
      return [];
    }
    setLoading(true);
    try {
      const data = await getInvoicesByPatientId(patientId);
      const rawList = Array.isArray(data)
        ? data
        : Array.isArray(data?.data)
        ? data.data
        : Array.isArray(data?.content)
        ? data.content
        : data
        ? [data]
        : [];
      const list = rawList.map((invoice) =>
        typeof invoice === "object" && invoice
          ? normalizeInvoiceRelationIds(invoice)
          : invoice
      );
      setInvoices(list);
      return list;
    } catch (error) {
      console.error("Failed to load patient invoices for print menu:", error);
      setInvoices([]);
      toast.error("Could not load invoices for printing");
      return [];
    } finally {
      setLoading(false);
    }
  }, [patientId]);

  useEffect(() => {
    setInvoices([]);
  }, [patientId]);

  useEffect(() => {
    if ((hideTrigger && externalOpen) || asField) {
      loadInvoices();
    }
  }, [hideTrigger, externalOpen, asField, loadInvoices]);

  const allowedInvoiceTypes = useMemo(() => {
    if (!Array.isArray(invoiceTypes) || invoiceTypes.length === 0) return null;
    return new Set(
      invoiceTypes
        .map((type) => String(type || "").trim().toUpperCase())
        .filter(Boolean)
    );
  }, [invoiceTypes]);

  const scopedInvoices = useMemo(() => {
    return (Array.isArray(invoices) ? invoices : [])
      .filter((invoice) => {
        if (!invoice) return false;
        const linkedToPatient = Boolean(
          invoice.patientId ??
            invoice.patient_id ??
            invoice.appointmentId ??
            invoice.appointment_id ??
            invoice.prescriptionId ??
            invoice.prescription_id
        );
        if (linkedToPatient) return true;
        return !isExpenseInvoice(invoice);
      })
      .filter((invoice) =>
        invoiceMatchesScope(invoice, {
          dateKey,
          admissionId,
          appointmentId,
          prescriptionId,
          prescriptionIds,
          appointmentDateTime,
          siblingAppointments,
        })
      )
      .filter((invoice) => {
        if (!allowedInvoiceTypes) return true;
        return allowedInvoiceTypes.has(getPatientInvoicePrintKey(invoice));
      })
      .sort((a, b) => {
        const keyA = getPatientInvoicePrintKey(a);
        const keyB = getPatientInvoicePrintKey(b);
        const orderA = TYPE_ORDER.indexOf(keyA);
        const orderB = TYPE_ORDER.indexOf(keyB);
        const rankA = orderA === -1 ? TYPE_ORDER.length : orderA;
        const rankB = orderB === -1 ? TYPE_ORDER.length : orderB;
        if (rankA !== rankB) return rankA - rankB;
        return String(getInvoiceNumber(a)).localeCompare(
          String(getInvoiceNumber(b))
        );
      });
  }, [invoices, dateKey, admissionId, appointmentId, prescriptionId, prescriptionIds, appointmentDateTime, siblingAppointments, allowedInvoiceTypes]);

  const typeCounts = useMemo(() => {
    const counts = {};
    scopedInvoices.forEach((invoice) => {
      const key = getPatientInvoicePrintKey(invoice);
      counts[key] = (counts[key] || 0) + 1;
    });
    return counts;
  }, [scopedInvoices]);

  const printableLabReports = useMemo(() => {
    if (!showLabReports) return [];
    const list = dedupeLabRecords(Array.isArray(labReports) ? labReports : []);
    return list.filter(
      (report) =>
        report &&
        String(report.testName || report.name || "").trim() &&
        hasPrintableLabResult(report)
    );
  }, [labReports, showLabReports]);

  const resolveFullPatient = useCallback(async () => {
    const id = patient?.id ?? patient?.patientId ?? patientId;
    let fullPatient = patient || null;

    if (id != null && id !== "") {
      try {
        const fetched = await getPatientById(id);
        if (fetched) {
          fullPatient = { ...(patient || {}), ...fetched };
        }
      } catch (error) {
        console.warn(
          "[PatientTimelinePrintMenu] Could not refresh patient for print:",
          error
        );
      }
    }

    if (!fullPatient && id != null) {
      fullPatient = { id };
    }

    return fullPatient;
  }, [patient, patientId]);

  const buildPatientsLookup = (fullPatient) => {
    if (!fullPatient) return {};
    const keys = [
      fullPatient.id,
      fullPatient.patientId,
      patientId,
      fullPatient.id != null ? String(fullPatient.id) : null,
      patientId != null ? String(patientId) : null,
    ].filter((key) => key != null && key !== "");

    const lookup = {};
    keys.forEach((key) => {
      lookup[key] = fullPatient;
    });
    return lookup;
  };

  const enrichInvoiceForPrint = (invoice, fullPatient) => {
    const resolvedPatientId =
      invoice?.patientId ??
      invoice?.patient_id ??
      fullPatient?.id ??
      fullPatient?.patientId ??
      patientId;
    const contextDoctor = resolveDoctorNameFromContext(doctorName);
    const invoiceDoctor = resolveDoctorNameFromContext(
      invoice?.doctorName || invoice?.doctor_name
    );

    return {
      ...invoice,
      patientId: resolvedPatientId,
      patient_id: resolvedPatientId,
      patientName:
        invoice?.patientName ||
        invoice?.patient_name ||
        invoice?.toName ||
        fullPatient?.name ||
        "",
      doctorName: invoiceDoctor || contextDoctor || invoice?.doctorName,
      doctor_name: invoiceDoctor || contextDoctor || invoice?.doctor_name,
    };
  };

  const handleOpen = async (event) => {
    setAnchorEl(event.currentTarget);
    await loadInvoices();
  };

  const closeMenu = () => {
    setAnchorEl(null);
    if (typeof onMenuClose === "function") onMenuClose();
  };

  const handleClose = () => {
    if (printing) return;
    closeMenu();
  };

  const handlePrintSummary = () => {
    closeMenu();
    if (typeof onPrintSummary === "function") onPrintSummary();
  };

  const handleExportPdf = () => {
    closeMenu();
    if (typeof onExportPdf === "function") onExportPdf();
  };

  const handlePrintInvoice = async (invoice) => {
    if (!invoice) return;
    setPrinting(true);
    try {
      const fullPatient = await resolveFullPatient();
      const enrichedInvoice = enrichInvoiceForPrint(invoice, fullPatient);

      await printInvoiceFromTable(enrichedInvoice, {
        user,
        patientsLookup: buildPatientsLookup(fullPatient),
        // Do not pass day lab reports as preferred invoice lines — that can
        // replace full billed tests with a partial completed-report list.
        preferredPathologyTests: null,
      });
      closeMenu();
    } catch (error) {
      console.error("Failed to print invoice from timeline:", error);
      toast.error(error?.message || "Failed to print invoice");
    } finally {
      setPrinting(false);
    }
  };

  const handlePrintLabReport = async (report) => {
    if (!report) return;
    if (!hasPrintableLabResult(report)) {
      toast.info("Print is available once the lab report is completed.");
      return;
    }

    setPrinting(true);
    try {
      const fullPatient = await resolveFullPatient();
      const contextDoctor = resolveDoctorNameFromContext(doctorName);

      let hospitalAddress = "";
      let hospitalPhone = "";
      const organizationId = getOrganizationId(user);
      if (organizationId) {
        try {
          const profile = await fetchOrganizationProfile(organizationId);
          hospitalAddress = profile?.address || "";
          hospitalPhone = profile?.phone || "";
        } catch (error) {
          console.warn(
            "[PatientTimelinePrintMenu] Could not load org profile for lab report:",
            error
          );
        }
      }

      printPathologyLabReport({
        report: {
          ...report,
          testName: report.testName || report.name,
          patientId:
            report.patientId ||
            fullPatient?.id ||
            fullPatient?.patientId ||
            patientId,
          patientName: report.patientName || fullPatient?.name,
          doctorName:
            resolveDoctorNameFromContext(report.doctorName) ||
            resolveDoctorNameFromContext(report.resolvedDoctorName) ||
            contextDoctor,
        },
        patient: fullPatient,
        organizationName: getOrganizationName(user),
        hospitalAddress,
        hospitalPhone,
        referringDoctorName: contextDoctor,
      });
      closeMenu();
    } catch (error) {
      console.error("Failed to print lab report from timeline:", error);
      toast.error(error?.message || "Failed to print lab report");
    } finally {
      setPrinting(false);
    }
  };

  const menuOpen = hideTrigger ? Boolean(externalOpen) : open;
  const menuAnchor = hideTrigger ? externalAnchorEl : anchorEl;

  if (asField) {
    const documentOptions = [
      {
        id: "summary",
        group: "Prescription",
        label: "Print Summary",
        run: handlePrintSummary,
      },
      ...(typeof onExportPdf === "function"
        ? [
            {
              id: "pdf",
              group: "Prescription",
              label: "Export to PDF",
              run: handleExportPdf,
            },
          ]
        : []),
      ...printableLabReports.map((report, index) => ({
        id: `lab-${report.id || report.reportId || index}`,
        group: "Lab Test Reports",
        label: `${report.testName || report.name || "Lab Test"} Report`,
        run: () => handlePrintLabReport(report),
      })),
      ...scopedInvoices.map((invoice) => {
        const label = getPatientInvoicePrintLabel(invoice);
        const number = getInvoiceNumber(invoice);
        const key = getPatientInvoicePrintKey(invoice);
        const showNumber = typeCounts[key] > 1 && number;
        return {
          id: `inv-${invoice.id ?? number}-${key}`,
          group: "Invoices",
          label: showNumber ? `${label} (${number})` : label,
          run: () => handlePrintInvoice(invoice),
        };
      }),
    ];

    return (
      <Autocomplete
        size="small"
        options={documentOptions}
        groupBy={(option) => option.group}
        getOptionLabel={(option) => option.label || ""}
        isOptionEqualToValue={(option, value) => option.id === value.id}
        value={null}
        blurOnSelect
        loading={loading}
        disabled={printing}
        onChange={(_event, option) => {
          if (option?.run) option.run();
        }}
        sx={{
          flex: "1 1 240px",
          minWidth: { xs: "100%", sm: 220 },
          "& .MuiOutlinedInput-root": {
            backgroundColor: "#fff",
            fontSize: "0.875rem",
          },
          ...sx,
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            size="small"
            label={fieldLabel}
            placeholder={fieldLabel}
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {loading || printing ? <CircularProgress color="inherit" size={16} /> : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
      />
    );
  }

  return (
    <>
      {hideTrigger ? null : iconOnly ? (
        <IconButton
          size={size}
          color={color}
          onClick={handleOpen}
          disabled={printing}
          aria-label="Print"
          sx={sx}
        >
          <PrintIcon sx={{ fontSize: 14 }} />
        </IconButton>
      ) : (
        <Button
          size={size}
          variant={variant}
          color={color}
          onClick={handleOpen}
          endIcon={<ArrowDropDownIcon />}
          startIcon={<PrintIcon fontSize="small" />}
          disabled={printing}
          sx={sx}
        >
          Print
        </Button>
      )}
      <Menu
        anchorEl={menuAnchor}
        open={menuOpen && Boolean(menuAnchor)}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: hideTrigger ? "left" : "right" }}
        transformOrigin={{ vertical: "top", horizontal: hideTrigger ? "left" : "right" }}
        PaperProps={
          hideTrigger
            ? { sx: { minWidth: menuAnchor?.clientWidth || 280, mt: 0.5 } }
            : undefined
        }
      >
        <MenuItem onClick={handlePrintSummary} disabled={printing}>
          <ListItemText primary="Print Summary" />
        </MenuItem>
        {typeof onExportPdf === "function" && (
          <MenuItem onClick={handleExportPdf} disabled={printing}>
            <ListItemText primary="Export to PDF" />
          </MenuItem>
        )}

        {/* Lab reports come from the labReports prop — already fetched by the
            timeline itself, no request happens here — so this section renders
            immediately and must not wait on the invoices fetch below. */}
        {printableLabReports.length > 0 && [
          <Divider key="divider-reports" />,
          <MenuItem key="reports-header" disabled sx={{ opacity: 1 }}>
            <Typography variant="caption" color="text.secondary">
              Lab Test Reports
            </Typography>
          </MenuItem>,
          ...printableLabReports.map((report, index) => {
            const testName = report.testName || report.name || "Lab Test";
            return (
              <MenuItem
                key={`lab-report-${report.id || report.reportId || index}-${testName}`}
                onClick={() => handlePrintLabReport(report)}
                disabled={printing}
              >
                <ListItemText
                  primary={`${testName} Report`}
                  secondary="Pathology lab report"
                />
              </MenuItem>
            );
          }),
        ]}

        {loading ? (
          <MenuItem disabled>
            <CircularProgress size={16} sx={{ mr: 1 }} />
            <ListItemText primary="Loading invoices..." />
          </MenuItem>
        ) : (
          [
            scopedInvoices.length > 0 ? (
              <Divider key="divider-invoices" />
            ) : (
              <Divider key="divider-empty-inv" />
            ),
            scopedInvoices.length > 0 ? (
              <MenuItem key="invoices-header" disabled sx={{ opacity: 1 }}>
                <Typography variant="caption" color="text.secondary">
                  Invoices
                </Typography>
              </MenuItem>
            ) : (
              <MenuItem key="empty-invoices" disabled>
                <ListItemText primary="No invoices for this visit" />
              </MenuItem>
            ),
            ...scopedInvoices.map((invoice) => {
              const label = getPatientInvoicePrintLabel(invoice);
              const number = getInvoiceNumber(invoice);
              const key = getPatientInvoicePrintKey(invoice);
              const showNumber = typeCounts[key] > 1 && number;
              return (
                <MenuItem
                  key={`inv-${invoice.id ?? number}-${key}`}
                  onClick={() => handlePrintInvoice(invoice)}
                  disabled={printing}
                >
                  <ListItemText
                    primary={showNumber ? `${label} (${number})` : label}
                    secondary={
                      !showNumber && number ? `Invoice ${number}` : undefined
                    }
                  />
                </MenuItem>
              );
            }),
          ]
        )}
      </Menu>
    </>
  );
};

export default PatientTimelinePrintMenu;
